/**
 * @fileoverview
 * Response Interceptor - Unwraps standardized API response format
 * Extracts data from { statusCode, status, message, data } format
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { HttpInterceptorFn, HttpEvent, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { map } from 'rxjs/operators';
import { ApiSuccessResponse, isSuccessResponse } from '../interfaces/api-response.interface';
import { NotificationService } from '@core/services';
import { SKIP_SUCCESS_TOAST } from '../context/skip-success-toast.context';

/**
 * Extended HttpResponse with API response metadata
 */
interface HttpResponseWithMetadata<T> extends HttpResponse<T> {
  _apiResponse?: ApiSuccessResponse<T>;
}

/** Some endpoints return HTTP 200 with `{ success: false, ... }` in `data` (e.g. connection test). */
function isLogicalFailurePayload(data: unknown): boolean {
  return (
    !!data &&
    typeof data === 'object' &&
    'success' in data &&
    (data as { success: boolean }).success === false
  );
}

/**
 * Response Interceptor
 * Unwraps the standardized API response format:
 * { statusCode, status, message, data } -> data
 * 
 * This allows services to work with the actual data directly
 * while still having access to metadata when needed
 * 
 * Also shows success toast notifications for successful POST, PUT, PATCH, DELETE operations
 */
export const responseInterceptor: HttpInterceptorFn = (req, next) => {
  const notificationService = inject(NotificationService);
  const method = req.method.toUpperCase();
  const showSuccessToast = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);

  return next(req).pipe(
    map((response: HttpEvent<any>) => {
      // Only transform HttpResponse objects (not HttpHeaderResponse, etc.)
      if (response instanceof HttpResponse) {
        const body = response.body;
        
        // Handle 204 NO_CONTENT responses (DELETE operations)
        // Backend interceptor transforms these to standardized format, but body might be null
        if (response.status === 204 && showSuccessToast) {
          // For DELETE operations with 204, show success message
          const message = (body && isSuccessResponse(body) && body.message) 
            ? body.message 
            : 'Resource deleted successfully';
          notificationService.success(message, 'Success');
          
          // Return response with null body (204 standard)
          return response.clone({ body: null });
        }
        
        // Check if response follows the standardized format
        if (body && isSuccessResponse(body)) {
          // Clone the response with unwrapped data
          const clonedResponse = response.clone({
            body: body.data,
          }) as HttpResponseWithMetadata<any>;
          
          // Add metadata as a custom property
          clonedResponse._apiResponse = body;
          
          // Show success toast for successful mutations (skip logical failure / soft-confirm)
          if (showSuccessToast && response.status >= 200 && response.status < 300) {
            const data = body.data;
            const isSoftConfirm =
              !!data &&
              typeof data === 'object' &&
              (data as { requiresConfirmation?: boolean }).requiresConfirmation === true;
            const skipSuccessToast = req.context.get(SKIP_SUCCESS_TOAST);
            if (
              !skipSuccessToast &&
              !isLogicalFailurePayload(data) &&
              !isSoftConfirm
            ) {
              const message = body.message || 'Operation completed successfully';
              notificationService.success(message, 'Success');
            }
          }
          
          return clonedResponse;
        }
      }
      
      // If not in standardized format, return as-is (backward compatibility)
      return response;
    })
  );
};
